import { FeedbackState } from "@c/shared";
import { cn } from "@u/cn";
import { Panel } from "@ui/panel";
import { Skeleton } from "@ui/skeleton";
import { type ReactNode } from "react";

export interface DashboardDetailSection {
  id: string;
  title?: ReactNode;
  /** Optional control rendered at the end of the title row (e.g. a link). */
  action?: ReactNode;
  content: ReactNode;
}

export interface DashboardDetailPanelProps {
  header: ReactNode;
  actions?: ReactNode;
  sections?: DashboardDetailSection[];
  loading?: boolean;
  empty?: { title: string; description?: string };
  className?: string;
  children?: ReactNode;
}

/**
 * Right-hand panel for master/detail layouts (pair with a list on the left,
 * e.g. inside `DashboardPage`'s `rail`-less main + a list). Header + actions
 * bar, then either `sections` or free `children`, with loading/empty states.
 */
export function DashboardDetailPanel({
  header,
  actions,
  sections,
  loading,
  empty,
  className,
  children,
}: DashboardDetailPanelProps) {
  let body: ReactNode;
  if (loading) {
    body = (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-6 w-40 rounded-lg" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  } else if (empty) {
    body = (
      <FeedbackState
        variant="empty"
        surface="plain"
        title={empty.title}
        description={empty.description}
      />
    );
  } else if (sections && sections.length > 0) {
    body = (
      <div className="space-y-8">
        {sections.map((section) => (
          <section key={section.id} className="space-y-3">
            {(section.title || section.action) && (
              <div className="flex items-center justify-between gap-2">
                {section.title ? (
                  <h3 className="label-micro">{section.title}</h3>
                ) : (
                  <span />
                )}
                {section.action}
              </div>
            )}
            {section.content}
          </section>
        ))}
      </div>
    );
  } else {
    body = children;
  }

  return (
    <Panel className={cn("p-0 overflow-hidden", className)}>
      {/* Barra de header de la card: contenido a la izquierda, acciones a la
          derecha. Vivia en `cardChrome.tsx` cuando la compartian tres
          componentes; `DashboardCard`/`DashboardChartCard` se borraron el
          2026-08-31 (cero consumidores) y quedo un solo caller. */}
      <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-border/40">
        <div className="min-w-0 flex-1">{header}</div>
        {actions && (
          <div className="shrink-0 flex items-center gap-2">{actions}</div>
        )}
      </div>
      <div className="p-6">{body}</div>
    </Panel>
  );
}
